import { GenericModelApi } from "./GenericModelApi";
import { ApiSchema } from "./ApiClient";
import { UserFormData } from "@/formTypes/UserDataType";

export type UserDataType = Partial<ApiSchema["User"]["type"] & UserFormData>;

/** Normaliza un correo para compararlo: sin espacios y en minusculas. */
export const normalizeEmail = (email: string | null | undefined): string =>
    `${email ?? ""}`.trim().toLowerCase();

/**
 * La validacion de unicidad corre en cada tecleo del formulario, asi que la lista del tenant
 * se reutiliza por unos segundos en vez de consultarla una vez por pulsacion. Si queda
 * desactualizada, la lambda de Cognito sigue siendo la que rechaza el duplicado.
 */
const EMAIL_LOOKUP_TTL_MS = 10_000;

class Api extends GenericModelApi<"User", UserDataType> {
    private emailLookup?: { promise: Promise<UserDataType[]>, expiresAt: number };

    private async listForEmailLookup(): Promise<UserDataType[]> {
        const now = Date.now();
        if (!this.emailLookup || this.emailLookup.expiresAt <= now) {
            const promise = this.fetchAll({}, "", 1000)
                .then(({ items = [] }) => items)
                .catch((error) => {
                    this.emailLookup = undefined;
                    throw error;
                });
            this.emailLookup = { promise, expiresAt: now + EMAIL_LOOKUP_TTL_MS };
        }
        return this.emailLookup.promise;
    }

    /** Lista los usuarios de una suscripcion por su tenantId (owner), para uso admin. */
    async fetchByTenantId(tenantId: string, filters?: any, selectionSet?: any) {
        return this.fetchAllByOwnerId(tenantId, filters, selectionSet);
    }

    /**
     * Busca un usuario del tenant por correo. El correo es el username en Cognito, asi que
     * no puede repetirse; se compara normalizado porque Cognito no distingue mayusculas.
     * `excludeId` sirve para ignorar el propio registro al editar.
     */
    async findByEmail(email: string, excludeId?: string): Promise<UserDataType | undefined> {
        const target = normalizeEmail(email);
        if (!target) {
            return undefined;
        }
        const users = await this.listForEmailLookup();
        return users.find(
            (user) => normalizeEmail(user.email) === target && user.id !== excludeId
        );
    }

    /** Invalida la lista cacheada para la validacion de unicidad del correo. */
    clearEmailLookupCache() {
        this.emailLookup = undefined;
    }
}
export const UserApi = new Api("User", "name", "ASC");
